import {
  Component,
  computed,
  ElementRef,
  forwardRef,
  HostListener,
  input,
  output,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
 
@Component({
  selector: 'app-multi-select',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './multi-select.component.html',
  styleUrls: ['./multi-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectComponent),
      multi: true,
    },
  ],
})
export class MultiSelectComponent<T extends Record<string, any>> implements ControlValueAccessor {
  /** The list of available options to display in the dropdown. */
  readonly options = input<T[]>([]);
 
  /** The property name on T to use as the display label. Defaults to 'name'. */
  readonly displayPath = input<string>('name');
 
  /** Placeholder text shown when nothing is selected. */
  readonly placeholder = input<string>('Select…');
 
  /** Whether the trigger renders with an underline border (edit mode). */
  readonly editMode = input<boolean>(false);
 
  /** Emits the full array of selected items whenever the selection changes. */
  readonly selectionChange = output<T[]>();
 
  public isOpen = false;
  public disabled = false;
 
  private readonly _selectedItems = signal<T[]>([]);
 
  readonly selectedItems = this._selectedItems.asReadonly();
 
  readonly displayLabel = computed(() => {
    const items = this._selectedItems();
    if (items.length === 0) return '';
    if (items.length === 1) return String(items[0][this.displayPath()]);
    return `${items[0][this.displayPath()]} +${items.length - 1}`;
  });
 
  onChange: (value: T[]) => void = () => {};
  onTouched: () => void = () => {};
 
  constructor(private elementRef: ElementRef) {}
 
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }
 
  /** outside edit mode (and for read-only users) the selection is only shown */
  private get canChange(): boolean {
    return this.editMode() && !this.disabled;
  }

  public toggleDropdown(): void {
    if (!this.canChange) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
        setTimeout(() => {
            this.positionDropdown();
            const dropdown = this.elementRef.nativeElement.querySelector('.dropdown');
            if (dropdown) dropdown.style.visibility = 'visible';
        });
    }
  }
 
  private positionDropdown(): void {
    const trigger = this.elementRef.nativeElement.querySelector('.trigger');
    const dropdown = this.elementRef.nativeElement.querySelector('.dropdown');
    if (!trigger || !dropdown) return;
 
    dropdown.style.visibility = 'hidden';
    const rect: DOMRect = trigger.getBoundingClientRect();
    dropdown.style.top = `${rect.bottom}px`;
    dropdown.style.left = `${rect.left}px`;
    dropdown.style.width = `${rect.width}px`;
  }
 
  public isSelected(item: T): boolean {
    return this._selectedItems().some(s => s['id'] === item['id']);
  }
 
  public toggleItem(item: T): void {
    if (!this.canChange) return;
 
    const current = this._selectedItems();
    const next = this.isSelected(item)
      ? current.filter(s => s['id'] !== item['id'])
      : [...current, item];
 
    this._selectedItems.set(next);
    this.onChange(next);
    this.onTouched();
    this.selectionChange.emit(next);
  }
 
  writeValue(value: T[] | null): void {
    this._selectedItems.set(value ?? []);
  }
 
  registerOnChange(fn: (value: T[]) => void): void {
    this.onChange = fn;
  }
 
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
 
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}